import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import {
  AdminModal, ConfirmDialog, FormField,
  LoadingState, ErrorState, ApiModeBanner, inputCls,
} from "../components/AdminUI";
import { useAdminT } from "../../../hooks/useAdminT";
import { useToast } from "../../../components/base/Toast";
import { useAdminData } from "../../../store/adminDataStore";
import type { OrderCancellationLog } from "../../../store/types";

const NOW = () => new Date().toISOString();

function emptyLog(orderId = ""): OrderCancellationLog {
  return {
    id: `ocl-${Date.now()}`,
    order_id: orderId,
    cancelled_at: new Date().toISOString().slice(0, 16),
    cancelled_by: "",
    reason: "",
    refund_amount_twd: 0,
    penalty_amount_twd: 0,
    non_refundable_cost_twd: 0,
    note: "",
  };
}

function EditModal({
  form, onChange, onClose, onSave, saving, isNew, orders,
}: {
  form: OrderCancellationLog; onChange: (v: OrderCancellationLog) => void;
  onClose: () => void; onSave: () => void; saving: boolean; isNew: boolean;
  orders: Array<{ id: string; label: string }>;
}) {
  const t = useAdminT();
  const set = (field: keyof OrderCancellationLog, value: unknown) => onChange({ ...form, [field]: value });
  return (
    <AdminModal
      title={isNew ? t("tabifotoAdmin.order_cancellation_logs.add_log") : t("tabifotoAdmin.order_cancellation_logs.edit_log")}
      onClose={onClose} onSave={onSave} saving={saving}
    >
      <FormField label={t("tabifotoAdmin.order_cancellation_logs.order_id")} required>
        <select className={inputCls} value={form.order_id} onChange={(e) => set("order_id", e.target.value)}>
          <option value="">—</option>
          {orders.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
      </FormField>
      <div className="grid grid-cols-2 gap-4">
        <FormField label={t("tabifotoAdmin.order_cancellation_logs.cancelled_at")} required>
          <input className={inputCls} type="datetime-local" value={form.cancelled_at.slice(0, 16)}
            onChange={(e) => set("cancelled_at", e.target.value + ":00Z")} />
        </FormField>
        <FormField label={t("tabifotoAdmin.order_cancellation_logs.cancelled_by")} required>
          <select className={inputCls} value={form.cancelled_by} onChange={(e) => set("cancelled_by", e.target.value)}>
            <option value="">—</option>
            <option value="customer">{t("tabifotoAdmin.order_cancellation_logs.by_customer")}</option>
            <option value="ops">{t("tabifotoAdmin.order_cancellation_logs.by_ops")}</option>
          </select>
        </FormField>
      </div>
      <FormField label={t("tabifotoAdmin.order_cancellation_logs.reason")} required>
        <textarea className={`${inputCls} h-20 resize-none`} value={form.reason}
          onChange={(e) => set("reason", e.target.value)} maxLength={500} />
      </FormField>
      <div className="p-4 bg-[#F7F6F3] rounded-lg mb-4">
        <p className="text-xs uppercase tracking-wider text-[#2C2C2C]/40 font-light mb-3">
          {t("tabifotoAdmin.order_cancellation_logs.financial_summary")}
        </p>
        <div className="grid grid-cols-3 gap-3">
          <FormField label={t("tabifotoAdmin.order_cancellation_logs.refund_amount_twd")}>
            <input className={inputCls} type="number" min={0} value={form.refund_amount_twd}
              onChange={(e) => set("refund_amount_twd", Number(e.target.value))} />
          </FormField>
          <FormField label={t("tabifotoAdmin.order_cancellation_logs.penalty_amount_twd")}>
            <input className={inputCls} type="number" min={0} value={form.penalty_amount_twd}
              onChange={(e) => set("penalty_amount_twd", Number(e.target.value))} />
          </FormField>
          <FormField label={t("tabifotoAdmin.order_cancellation_logs.non_refundable_cost_twd")}>
            <input className={`${inputCls} border-red-200 focus:border-red-300`} type="number" min={0}
              value={form.non_refundable_cost_twd}
              onChange={(e) => set("non_refundable_cost_twd", Number(e.target.value))} />
          </FormField>
        </div>
      </div>
      <FormField label={t("tabifotoAdmin.order_cancellation_logs.note")}>
        <textarea className={`${inputCls} h-20 resize-none`} value={form.note}
          onChange={(e) => set("note", e.target.value)} maxLength={500} />
      </FormField>
    </AdminModal>
  );
}

function DetailModal({ log, order, onClose }: {
  log: OrderCancellationLog;
  order?: { order_number: string; customer_name: string };
  onClose: () => void;
}) {
  const t = useAdminT();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-lg w-full max-w-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#D9D9D9]/60">
          <div>
            <h2 className="text-base font-light text-[#2C2C2C]">{t("tabifotoAdmin.order_cancellation_logs.title")}</h2>
            {order && <p className="text-xs text-[#2C2C2C]/40 mt-0.5">{order.order_number} · {order.customer_name}</p>}
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center text-[#2C2C2C]/40 hover:text-[#2C2C2C] cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: t("tabifotoAdmin.order_cancellation_logs.col_refund"), value: log.refund_amount_twd, color: "text-emerald-600" },
              { label: t("tabifotoAdmin.order_cancellation_logs.col_penalty"), value: log.penalty_amount_twd, color: "text-amber-600" },
              { label: t("tabifotoAdmin.order_cancellation_logs.col_non_refundable"), value: log.non_refundable_cost_twd, color: "text-red-500" },
            ].map((item) => (
              <div key={item.label} className="p-3 bg-[#F7F6F3] rounded-lg text-center">
                <p className="text-xs text-[#2C2C2C]/40 font-light mb-1">{item.label}</p>
                <p className={`text-base font-light ${item.color}`}>NT${item.value.toLocaleString()}</p>
              </div>
            ))}
          </div>
          <div>
            <p className="text-xs text-[#2C2C2C]/40 uppercase tracking-wider mb-1.5">{t("tabifotoAdmin.order_cancellation_logs.reason")}</p>
            <p className="text-sm text-[#2C2C2C]/70 font-light leading-relaxed">{log.reason || "—"}</p>
          </div>
          {log.note && (
            <div>
              <p className="text-xs text-[#2C2C2C]/40 uppercase tracking-wider mb-1.5">{t("tabifotoAdmin.order_cancellation_logs.note")}</p>
              <p className="text-sm text-[#2C2C2C]/70 font-light leading-relaxed">{log.note}</p>
            </div>
          )}
          <div className="flex gap-4 text-xs text-[#2C2C2C]/40">
            <span>{t("tabifotoAdmin.order_cancellation_logs.cancelled_at")}: {log.cancelled_at.slice(0, 16).replace("T", " ")}</span>
            <span>{t("tabifotoAdmin.order_cancellation_logs.cancelled_by")}: {log.cancelled_by}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminCancellationLogsPage() {
  const t = useAdminT();
  const { state, status, isApiMode, upsertCancellationLog, removeCancellationLog, refetchCancellationLogs } = useAdminData();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preFilter = searchParams.get("order") ?? "all";

  const [editItem, setEditItem] = useState<OrderCancellationLog | null>(null);
  const [detailItem, setDetailItem] = useState<OrderCancellationLog | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [filterOrder, setFilterOrder] = useState<string>(preFilter);

  const orders = state.orders.map((o) => ({ id: o.id, label: `${o.order_number} — ${o.customer_name}` }));

  const filtered = [...state.orderCancellationLogs]
    .filter((l) => filterOrder === "all" || l.order_id === filterOrder)
    .sort((a, b) => b.cancelled_at.localeCompare(a.cancelled_at));

  const handleSave = async () => {
    if (!editItem) return;
    setSaving(true);
    try {
      await upsertCancellationLog(editItem);
      toast.success(t("tabifotoAdmin.common.save_success"));
      setEditItem(null);
    } catch { toast.error(t("tabifotoAdmin.common.save_error")); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await removeCancellationLog(deleteId);
      toast.success(t("tabifotoAdmin.common.delete_success"));
    } catch { toast.error(t("tabifotoAdmin.common.delete_error")); }
    finally { setDeleteId(null); }
  };

  return (
    <AdminLayout
      title={t("tabifotoAdmin.order_cancellation_logs.title")}
      subtitle={t("tabifotoAdmin.order_cancellation_logs.subtitle")}
      action={
        <button onClick={() => setEditItem(emptyLog(filterOrder !== "all" ? filterOrder : ""))}
          className="flex items-center gap-2 px-4 py-2 bg-[#2C2C2C] text-white text-sm rounded-md hover:bg-[#2C2C2C]/80 transition-colors cursor-pointer whitespace-nowrap">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-base" /></div>
          {t("tabifotoAdmin.order_cancellation_logs.add_log")}
        </button>
      }
    >
      <ApiModeBanner isApiMode={isApiMode} />

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex gap-1 bg-[#F7F6F3] p-1 rounded-lg">
          <button onClick={() => setFilterOrder("all")}
            className={`px-3 py-1.5 rounded-md text-xs font-light transition-colors cursor-pointer whitespace-nowrap ${filterOrder === "all" ? "bg-white text-[#2C2C2C]" : "text-[#2C2C2C]/40 hover:text-[#2C2C2C]/70"}`}>
            {t("tabifotoAdmin.order_cancellation_logs.filter_all")}
          </button>
          {orders.map((o) => (
            <button key={o.id} onClick={() => setFilterOrder(o.id)}
              className={`px-3 py-1.5 rounded-md text-xs font-light transition-colors cursor-pointer whitespace-nowrap ${filterOrder === o.id ? "bg-white text-[#2C2C2C]" : "text-[#2C2C2C]/40 hover:text-[#2C2C2C]/70"}`}>
              {o.label.split(" — ")[0]}
            </button>
          ))}
        </div>
      </div>

      {status.cancellationLogs.loading ? <LoadingState /> :
        status.cancellationLogs.error ? <ErrorState message={status.cancellationLogs.error} onRetry={refetchCancellationLogs} /> : (
          <div className="space-y-2">
            {filtered.length === 0 ? (
              <div className="bg-white rounded-lg border border-[#D9D9D9]/60 py-12 text-center text-[#2C2C2C]/30 text-sm font-light">
                {t("tabifotoAdmin.order_cancellation_logs.no_logs")}
              </div>
            ) : filtered.map((log) => {
              const order = state.orders.find((o) => o.id === log.order_id);
              return (
                <div key={log.id} className="bg-white rounded-lg border border-[#D9D9D9]/60 p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <button onClick={() => navigate(`/admin/orders?order=${log.order_id}`)}
                          className="text-xs font-mono text-[#C8B8A6] hover:text-[#2C2C2C] cursor-pointer whitespace-nowrap">
                          {order ? order.order_number : log.order_id}
                        </button>
                        <span className="text-xs text-[#2C2C2C]/30">·</span>
                        <span className="text-xs text-[#2C2C2C]/40">{log.cancelled_at.slice(0, 10)} · {log.cancelled_by}</span>
                      </div>
                      <p className="text-sm text-[#2C2C2C]/70 font-light leading-relaxed line-clamp-2 mb-3">{log.reason}</p>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-[#2C2C2C]/40">{t("tabifotoAdmin.order_cancellation_logs.col_refund")}:</span>
                          <span className="text-sm font-light text-emerald-600">NT${log.refund_amount_twd.toLocaleString()}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-[#2C2C2C]/40">{t("tabifotoAdmin.order_cancellation_logs.col_penalty")}:</span>
                          <span className="text-sm font-light text-amber-600">NT${log.penalty_amount_twd.toLocaleString()}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-[#2C2C2C]/40">{t("tabifotoAdmin.order_cancellation_logs.col_non_refundable")}:</span>
                          <span className={`text-sm font-light ${log.non_refundable_cost_twd > 0 ? "text-red-500" : "text-[#2C2C2C]/40"}`}>
                            NT${log.non_refundable_cost_twd.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => setDetailItem(log)}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-[#2C2C2C]/40 hover:text-[#2C2C2C] hover:bg-[#F7F6F3] transition-colors cursor-pointer">
                        <i className="ri-eye-line text-base" />
                      </button>
                      <button onClick={() => setEditItem({ ...log })}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-[#2C2C2C]/40 hover:text-[#2C2C2C] hover:bg-[#F7F6F3] transition-colors cursor-pointer">
                        <i className="ri-edit-line text-base" />
                      </button>
                      <button onClick={() => setDeleteId(log.id)}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-[#2C2C2C]/40 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer">
                        <i className="ri-delete-bin-line text-base" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

      {editItem && (
        <EditModal form={editItem} onChange={setEditItem} onClose={() => setEditItem(null)}
          onSave={handleSave} saving={saving}
          isNew={!state.orderCancellationLogs.some((l) => l.id === editItem.id)}
          orders={orders} />
      )}
      {detailItem && (
        <DetailModal log={detailItem}
          order={state.orders.find((o) => o.id === detailItem.order_id)}
          onClose={() => setDetailItem(null)} />
      )}
      {deleteId && (
        <ConfirmDialog title={t("tabifotoAdmin.order_cancellation_logs.delete_confirm")}
          message={t("tabifotoAdmin.common.cannot_undone")} danger
          onConfirm={handleDelete} onCancel={() => setDeleteId(null)} />
      )}
    </AdminLayout>
  );
}
